<template>
  <div>
    <b-card>
      <b-button
        variant="outline-primary"
        class="push-right rounded-pill"
        v-show="tabIndex === 2"
        @click="goToHistoryAjust()"
        v-if="tabsValidation.indemnifications"
      >
        <feather-icon icon="ClockIcon" /> Historial de ajustes
      </b-button>
      <!-- Loading skeleton - only show when loading initial data and we don't know status yet -->
      <template v-if="!hasBillingData && loadingInitialData">
        <div class="d-flex">
          <b-skeleton width="15%" height="2.3rem" class="mb-05" />
          <b-skeleton width="23%" height="2.3rem" class="mb-05 mx-1" />
          <b-skeleton width="18%" height="2.3rem" class="mb-05 mr-1" />
          <b-skeleton width="25%" height="2.3rem" class="mb-05" />
        </div>
        <div class="p-2">
          <div class="table-render-skeleton">
            <b-skeleton-table :rows="10" :columns="7" :table-props="{}" />
          </div>
        </div>
      </template>
      <!-- Tabs with content -->
      <b-tabs v-else align="left" class="tab-primary" v-model="tabIndex">
        <b-tab v-if="shouldShowInvoices">
          <template #title>
            <div class="tab-title">
              <feather-icon icon="FileTextIcon" />
              <span>{{ $t('Facturas') }}</span>
            </div>
          </template>
          <billing-list
            :company_id="company_id"
            :country_code="country_code"
            v-if="tabsValidation.invoices"
          ></billing-list>
          <div v-else class="tab-no-access-container">
            <div class="no-access-content">
              <feather-icon
                :icon="
                  isUserSuperAdmin && !hasBusinessData && companyData && !companyData.showInvoices
                    ? 'AlertCircleIcon'
                    : 'LockIcon'
                "
                size="48"
                class="mb-2 warning-icon"
              />
              <template
                v-if="
                  isUserSuperAdmin && !hasBusinessData && companyData && !companyData.showInvoices
                "
              >
                <h3>No se puede listar facturas</h3>
                <p>La compañía consultada no posee datos de facturación</p>
              </template>
              <template v-else>
                <h3>{{ $t('Facturas no disponibles') }}</h3>
                <p>
                  {{
                    $t(
                      'Actualmente no tiene configurado el acceso a esta sección. Por favor, contacte a su ejecutivo comercial para poder visualizar sus facturas.'
                    )
                  }}
                </p>
              </template>
            </div>
          </div>
        </b-tab>
        <b-tab v-if="shouldShowNotes">
          <template #title>
            <div class="tab-title">
              <feather-icon icon="FileTextIcon" />
              <span v-if="isColombiaCompany"> {{ $t('Notas de crédito') }}</span>
              <span v-else> {{ $t('Notas de crédito y débito') }}</span>
            </div>
          </template>
          <notes-list
            :company_id="company_id"
            :is-colombia-company="isColombiaCompany"
            v-if="tabsValidation.notes"
          ></notes-list>
          <div v-else class="tab-no-access-container">
            <div class="no-access-content">
              <feather-icon
                :icon="isUserSuperAdmin && !hasBusinessData ? 'AlertCircleIcon' : 'LockIcon'"
                size="48"
                class="mb-2 warning-icon"
              />
              <template v-if="isUserSuperAdmin && !hasBusinessData">
                <h3>No se puede listar notas</h3>
                <p>La compañía consultada no posee datos de facturación</p>
              </template>
              <template v-else>
                <h3>{{ $t('Notas no disponibles') }}</h3>
                <p>
                  {{
                    $t(
                      'Actualmente no tiene configurado el acceso a esta sección. Por favor, contacte a su ejecutivo comercial para poder visualizar sus notas.'
                    )
                  }}
                </p>
              </template>
            </div>
          </div>
        </b-tab>
        <b-tab
          v-if="show.indemnifications && show.isBusinessDataAvailable && !isColombiaCompany">
          <template #title>
            <div class="tab-title">
              <feather-icon icon="FileTextIcon" />
              <span>{{ $t('Indemnizaciones') }}</span>
            </div>
          </template>
          <indemnifications-list
            :company_id="company_id"
            v-if="tabsValidation.indemnifications"
          ></indemnifications-list>
          <div v-else class="tab-no-access-container">
            <div class="no-access-content">
              <feather-icon
                :icon="isUserSuperAdmin && !hasBusinessData ? 'AlertCircleIcon' : 'LockIcon'"
                size="48"
                class="mb-2 warning-icon"
              />
              <template v-if="isUserSuperAdmin && !hasBusinessData">
                <h3>No se puede listar facturas</h3>
                <p>La compañía consultada no posee datos de facturación</p>
              </template>
              <template v-else>
                <h3>{{ $t('Indemnizaciones no disponibles') }}</h3>
                <p>
                  {{
                    $t(
                      'Actualmente no tiene configurado el acceso a esta sección. Por favor, contacte a su ejecutivo comercial para poder visualizar sus indemnizaciones.'
                    )
                  }}
                </p>
              </template>
            </div>
          </div>
        </b-tab>
        <b-tab v-if="shouldShowCollections" lazy>
          <template #title>
            <div class="tab-title">
              <feather-icon icon="FileTextIcon" />
              <span>{{ $t('Recaudos contra entrega') }}</span>
            </div>
          </template>
          <collections-list :company_id="company_id"></collections-list>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import BillingList from './billing/BillingList.vue'
import NotesList from './notes/NotesList.vue'
import IndemnificationsList from './indemnifications/IndemnificationsList.vue'
import CollectionsList from './collections/CollectionsList.vue'
import BillingService from '../billing.service'

export default {
  components: { BillingList, NotesList, IndemnificationsList, CollectionsList },

  data() {
    return {
      company_id: this.$route.params?.id,
      show: {
        indemnifications: false,
        collections: false,
        isBusinessDataAvailable: false
      },
      country_code: '',
      hasBillingData: false,
      loadingInitialData: true, // Flag to control when to show skeleton loader
      billingService: new BillingService(this),
      companyData: null,
      tabIndex: 0,
      countryConfig: {
        co: {
          showInvoices: true,
          showNotes: true,
          showCollections: true
        }
      },
      tabsValidation: {
        invoices: false,
        notes: false,
        indemnifications: false
      }
    }
  },
  computed: {
    ...mapGetters({
      mySession: 'getSession',
      hasBusinessData: 'getHasBusinessData'
    }),
    isUserSuperAdmin() {
      return ['superadmin', 'admin'].includes(this.$session.get('cas_user')?.role)
    },
    isExternalUser() {
      const role = this.$session.get('cas_user')?.role
      return !['superadmin', 'admin'].includes(role?.toLowerCase())
    },
    isColombiaCompany() {
      return (
        this.companyData &&
        this.companyData.country &&
        this.companyData.country.code.toLowerCase() === 'co'
      )
    },
    shouldShowInvoices() {
      return true
    },
    shouldShowNotes() {
      return true
    },
    shouldShowCollections() {
      return this.show.collections
    }
  },
  watch: {
    mySession() {
      if (this.mySession.id) this.getCompanyData()
      else this.hasBillingData = true
    },
    hasBusinessData() {
      this.show.isBusinessDataAvailable = this.hasBusinessData
    }
  },
  mounted() {
    this.environment = this.billingService.setEnvironment()
    if (this.mySession.id) this.getCompanyData()
    this.$bvToast.toast('Por favor, espere un momento', {
      title: 'Validando datos de la empresa',
      variant: 'info',
      id: 'bvToastBillingData'
    })
  },
  methods: {
    getCompanyData() {
      if (!this.companyData) this.$store.dispatch('fetchServiceStatusOnError', {
        name: 'getCompanyInfoBilling',
        params: { company_id: this.$route.params?.id },
        hideAlert: true,
        onSuccess: response => {
          // Turn off loading flag immediately when we get a response
          this.loadingInitialData = false

          this.$store.dispatch('changeValueHasBusinessData', response.data.hasBusinessData)
          this.$store.dispatch('changeValueHasShowInvoice', response.data.showInvoices)
          this.country_code = response.data.country.code.toLowerCase()
          this.callAllServices(response)
        },
        onError: e => this.onErrorBillingData(e)
      })
      if (this.isUserSuperAdmin) this.show.indemnifications = true
    },
    goToHistoryAjust() {
      this.$router.push({ name: 'historial-ajustes', params: { id: this.$route.params?.id } })
    },
    callAllServices(res) {
      this.companyData = res.data
      const countryCode = res.data.country.code.toLowerCase()
      this.country_code = countryCode

      const hasInvoicesAccess = res.data.showInvoices !== false
      const hasNotesAccess = res.data.showNotes !== false
      const hasIndemnificationsAccess =
        res.data.showIndemnifications !== false && !this.isColombiaCompany

      // Siempre mostrar los tabs, pero controlar el contenido con tabsValidation
      if (this.isUserSuperAdmin) {
        this.tabsValidation.invoices = this.hasBusinessData
        this.tabsValidation.notes = this.hasBusinessData
        this.tabsValidation.indemnifications = this.hasBusinessData
        this.show.collections = this.isColombiaCompany
      } else if (this.isColombiaCompany) {
        this.tabsValidation.invoices = this.hasBusinessData && hasInvoicesAccess
        this.tabsValidation.notes = this.hasBusinessData && hasNotesAccess
        this.tabsValidation.indemnifications = false
        this.show.collections = true
      } else {
        this.tabsValidation.invoices = this.hasBusinessData && hasInvoicesAccess
        this.tabsValidation.notes = this.hasBusinessData && hasNotesAccess
        this.tabsValidation.indemnifications = this.hasBusinessData && hasIndemnificationsAccess
        this.show.collections = false
      }

      // Restore tab selection from localStorage if available
      const savedTabIndex = localStorage.getItem('billingTabIndex')
      if (savedTabIndex !== null) {
        const parsedIndex = parseInt(savedTabIndex, 10)
        // Only set the saved tab if it's accessible
        if (
          (parsedIndex === 0 && this.shouldShowInvoices) ||
          (parsedIndex === 1 && this.shouldShowNotes) ||
          (parsedIndex === 2 && this.tabsValidation.indemnifications) ||
          (parsedIndex === 3 && this.shouldShowCollections)
        ) {
          this.tabIndex = parsedIndex
        }
      }

      // If collections is the only available tab, set it as active
      if (
        this.shouldShowCollections &&
        !this.tabsValidation.invoices &&
        !this.tabsValidation.notes &&
        !this.tabsValidation.indemnifications
      ) {
        this.tabIndex = 3 // Index of collections tab
      } else if (
        !this.shouldShowCollections &&
        !this.tabsValidation.invoices &&
        !this.tabsValidation.notes &&
        this.tabsValidation.indemnifications
      ) {
        this.tabIndex = 2 // Set to indemnifications if it's the only available tab
      } else if (
        !this.shouldShowCollections &&
        !this.tabsValidation.invoices &&
        this.tabsValidation.notes
      ) {
        this.tabIndex = 1 // Set to notes if it's the only available tab
      } else if (this.tabsValidation.invoices) {
        this.tabIndex = 0 // Default to invoices if available
      }

      // Save selected tab to localStorage
      localStorage.setItem('billingTabIndex', this.tabIndex.toString())
    },
    onErrorBillingData(error, noBillingData = true) {
      // Turn off all loading flags
      this.hasBillingData = true
      this.loadingInitialData = false

      // Disable all tab contents
      this.tabsValidation.invoices = false
      this.tabsValidation.notes = false
      this.tabsValidation.indemnifications = false

      // Hide loading toast
      this.$bvToast.hide('bvToastBillingData')

      // Show error alert for unexpected errors
      if (noBillingData) {
        this.$alert(
          this.$t('msg-problema-cargar', { code: error?.message || JSON.stringify(error) })
        )
      }
    },
    redirect() {
      const url = ['admin'].includes(this.$session.get('cas_user')?.role) ? '/manage-billing-data' : ''
      this.$router.push({ path: url })
    },
    isCountryWithSpecialConfig(countryCode) {
      return this.countryConfig[countryCode] !== undefined
    }
  }
}
</script>

<style lang="scss">
#swal2-title {
  font-size: 1.5rem;
  margin-bottom: 0;
}

.billings-button {
  top: 5.5rem !important;
  right: 2rem !important;
  position: absolute !important;
  z-index: 9;
  display: flex;
  gap: 0.5rem;
}

.no-access-message,
.tab-no-access-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  border-radius: 8px;
  margin: 1rem 0;

  .no-access-content {
    text-align: center;
    padding: 2rem;
    max-width: 500px;
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    border-left: 4px solid #ea5455;

    h3 {
      color: #5e5873;
      margin-bottom: 1rem;
      font-weight: 600;
    }

    p {
      color: #6e6b7b;
      margin-bottom: 0;
    }

    svg {
      color: #ea5455;
    }
  }
}

// Special styling for tab content
.tab-no-access-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 500px;
  width: 100%;
  background-color: transparent;
  padding: 2rem 0;

  .no-access-content {
    width: 100%;
    max-width: 100%;
    padding: 3rem 2rem;
    text-align: center;
    border: none;
    border-radius: 0;
    box-shadow: none;

    h3 {
      font-size: 1.75rem;
      margin-bottom: 1.5rem;
      color: #5e5873;
      font-weight: 600;
    }

    p {
      font-size: 1.1rem;
      line-height: 1.7;
      color: #6e6b7b;
      max-width: 600px;
      margin: 0 auto;
    }

    svg {
      margin-bottom: 1.5rem;
      height: 60px;
      width: 60px;
      color: #ea5455;
    }
  }
}

.warning-icon {
  color: #f18805;
  height: 2rem;
  width: 2rem;
  margin: auto;
  margin-bottom: 1em;
  stroke-width: 1;
  //fill: #F18805;
  stroke: #f18805;
}
</style>
